import { type ColumnDef } from '@tanstack/react-table';
import { Checkbox, Skeleton, Tag, Typography } from '@volvo/vcdk-react';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ILatestOperatingHours } from '@/service/vpc/types/ILatestOperatingHours';
import type { ITelemetryService } from '@/service/vpc/types/ITelemetryService';
import type { IChassisIdentifier, IUnit } from '@/service/vpc/types/IUnit';

import CampaignStatus from '@/components/CampaignStatus';
import Dot from '@/components/Dot';
import ProductDesignation from '@/components/ProductDesignation';
import UnitsFleets from '@/components/UnitsFleets';
import UserMetadataAvatar from '@/components/UserMetadataAvatar';
import { featuresName, isFeatureToggled } from '@/config/EnviromentConfig';
import useMediaQuery from '@/hooks/useMediaQuery';
import { chassis, telemetry as telemetryUtils, unitLocation } from '@/utils';

type TableColumnsProps = {
  latestOperatingHours?: ILatestOperatingHours[];
  isLoadingHours?: boolean;
  showInstallation?: boolean;
};

const useUnitsTable = ({ latestOperatingHours, isLoadingHours, showInstallation = true }: TableColumnsProps) => {
  const { t } = useTranslation();
  const { mediaQueryNodes: mq } = useMediaQuery();
  const [selectedRows, setSelectedRows] = useState<Record<number, boolean>>({});

  const getOperatingHoursForChassisId = (ci: IChassisIdentifier) => {
    return latestOperatingHours?.find((oh) => oh.ChassisId === `${ci.chassisSeries}${ci.chassisNumber}`)?.Value;
  };

  const columns = [
    {
      id: 'select',
      header: ({ table }) => {
        const selectableRows = table.getRowModel().rows;
        const allSelectableSelected = selectableRows.length > 0 && selectableRows.every((row) => row.getIsSelected());
        const someSelectableSelected = selectableRows.some((row) => row.getIsSelected());

        return (
          <Checkbox
            checked={allSelectableSelected}
            indeterminate={someSelectableSelected && !allSelectableSelected}
            onChange={() => {
              const rowIndices = selectableRows.map((row) => row.index);

              selectableRows.forEach((row) => {
                row.toggleSelected(!allSelectableSelected);
              });

              setSelectedRows((prevSelectedRows) => {
                if (!allSelectableSelected) {
                  // Add all row indices that aren't already selected
                  const newSelectedRows = { ...prevSelectedRows };
                  rowIndices.forEach((index) => {
                    newSelectedRows[index] = true;
                  });
                  return newSelectedRows;
                } else {
                  // Remove all row indices
                  return Object.fromEntries(
                    Object.entries(prevSelectedRows).filter(([index]) => !rowIndices.includes(Number(index)))
                  );
                }
              });
            }}
          />
        );
      },
      size: 45,
      maxSize: 45,
      enableResizing: false,
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onChange={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const isChecked = e.detail.checked;
            row.toggleSelected(isChecked);
            setSelectedRows((prevSelectedRows) => {
              if (isChecked) {
                // Now checked - add it
                return { ...prevSelectedRows, [row.index]: true };
              } else {
                // Now unchecked - remove it
                const newSelectedRows = { ...prevSelectedRows };
                delete newSelectedRows[row.index];
                return newSelectedRows;
              }
            });
          }}
        />
      ),
    },
    {
      accessorKey: 'unitName',
      id: 'unitName',
      header: t(($) => $.components.table.unit_table.unit_name),
      size: 250,
      maxSize: 250,
      enableGlobalFilter: true,
      meta: {
        hideLabelOnMobile: true,
      },
      sortingFn: (rowA, rowB) => {
        const unitA = rowA.original;
        const unitB = rowB.original;

        // Assign priority based on connectivity (green, gray, no dot)
        const getPriority = (unit: IUnit): number => {
          const hasDevice = telemetryUtils.getTelemetryDevices(unit.telemetryDeviceInfo).length > 0;
          const hasActive = telemetryUtils.hasActiveService(unit.serviceStatuses);
          if (!hasDevice || !hasActive) return 3;
          return 1;
        };

        const priorityA = getPriority(unitA);
        const priorityB = getPriority(unitB);

        // Sort by priority first
        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        // Within same priority, sort by connectivity status timestamp (most recent first)
        const lastSeenA = unitA.connectivityStatus?.statusTimestamp;
        const lastSeenB = unitB.connectivityStatus?.statusTimestamp;

        if (lastSeenA && lastSeenB) {
          const dateA = new Date(lastSeenA).getTime();
          const dateB = new Date(lastSeenB).getTime();
          if (dateA !== dateB) {
            return dateB - dateA; // Most recent first
          }
        } else if (lastSeenA && !lastSeenB) {
          return -1; // A has lastSeen, B doesn't - A comes first
        } else if (!lastSeenA && lastSeenB) {
          return 1; // B has lastSeen, A doesn't - B comes first
        }

        // Finally, sort by unitName
        return unitA.unitName.localeCompare(unitB.unitName);
      },
      filterFn: (row, _columnId, filterValue: string) => {
        const unit = row.original;
        const searchable = [
          unit.unitName,
          unit.metadata?.unitName,
          unit.metadata?.homePort?.address,
          unit.bestKnownLocation?.city,
          unit.bestKnownLocation?.country,
          unit.mainSegment,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return searchable.includes(filterValue.toLowerCase());
      },
      cell: (info) => {
        const row = info.row.original;

        // Dot rules:
        // - must have a telemetry device and an active service
        // - connectivityStatus.status === 'online' => green dot
        // - status === 'offline' or missing => grey dot
        const hasDevice = telemetryUtils.getTelemetryDevices(row.telemetryDeviceInfo).length > 0;
        const hasActive = telemetryUtils.hasActiveService(row.serviceStatuses);
        let dotVariant: 'success' | 'neutral' | null = null;

        if (hasDevice && hasActive) {
          dotVariant = telemetryUtils.getConnectivityStatusVariant(row.connectivityStatus?.status);
        }

        return (
          <div className="mb-6 flex flex-row items-center gap-5 whitespace-nowrap md:mb-0">
            <div className="relative">
              <UserMetadataAvatar size={48} url={row.metadata?.blobUrl} />
              {dotVariant && (
                <div className="absolute -right-2 -bottom-2">
                  <div className="relative flex aspect-square rounded-full border border-white bg-white p-px">
                    <Dot variant={dotVariant} />
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col">
              <Typography
                variant={mq({
                  default: 'heading4',
                  md: 'caption1',
                })}
                className="text-subtle truncate"
              >
                {row.metadata?.unitName || info.getValue<string>()}
              </Typography>
              <Typography variant="caption2" className="text-subtle truncate">
                {unitLocation.getUnitLocation(row)}
              </Typography>
            </div>
            {row.claiming && (
              <Tag startIcon="info" variant="neutral-subtle" size="small">
                {t(($) => $.components.table.unit_table.unverified_ownership)}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      accessorFn: (row) => row.serviceStatuses,
      id: 'digitalServices',
      header: t(($) => $.components.table.unit_table.digital_services),
      cell: (info) => {
        // Easy Connect units have their digital-service provisioning eligibility hidden.
        if (
          isFeatureToggled(featuresName.hideEasyConnectProvisioning) &&
          telemetryUtils.isEasyConnectUnit(info.row.original.telemetryDeviceInfo)
        ) {
          return (
            <Typography variant="caption1" className="text-subtle">
              -
            </Typography>
          );
        }

        const services = info.getValue<ITelemetryService[] | null>();

        if (!services || services.length === 0) {
          return (
            <Typography variant="caption1" className="text-subtle">
              -
            </Typography>
          );
        }

        // Count distinct serviceNames — the backend may return one row per market clone
        // of the same service (bug 1539968), and raw row counts inflate the fraction.
        const eligibleNames = new Set(
          services.filter((service) => service.eligibility?.isEligible === true).map((s) => s.serviceName)
        );

        if (eligibleNames.size === 0) {
          return (
            <Typography variant="caption1" className="text-subtle">
              -
            </Typography>
          );
        }

        // ponytail: hard cap — a unit exposes at most 2 digital services today (1 for an
        // EC_Wifi marine unit); bump when a third service launches. Belt-and-suspenders on
        // top of the distinct-name dedup (bug 1539968) so counts never exceed the catalog.
        const cap = eligibleNames.has('ActiveMonitoringEcWifi') ? 1 : 2;
        const denominator = Math.min(eligibleNames.size, cap);

        const activatedCount = Math.min(
          [...eligibleNames].filter((name) => services.some((s) => s.serviceName === name && s.status === 'Active'))
            .length,
          denominator
        );

        return (
          <Typography variant="caption1" className="text-subtle">
            {activatedCount}/{denominator}
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) =>
        chassis
          .getUnitsChassisIds(row)
          .map((ci) => `${ci}`)
          .join(', '),
      id: 'chassisIdentifiers',
      header: 'ChassisId',
      enableHiding: true,
      enableGlobalFilter: true,
      meta: {
        hideOnMobile: true,
      },
      cell: (info) => {
        return <Typography>{info.getValue<string>()}</Typography>;
      },
    },
    {
      accessorFn: (row) => row.unitModel,
      id: 'unitModelBrand',
      header: t(($) => $.components.table.unit_table.model_brand),
      sortingFn: (rowA, rowB) => {
        return rowA.original.brand.localeCompare(rowB.original.brand);
      },
      enableGlobalFilter: true,
      filterFn: (row, _columnId, filterValue: string) => {
        const unit = row.original;
        const searchable = [unit.unitModel, unit.brand].filter(Boolean).join(' ').toLowerCase();
        return searchable.includes(filterValue.toLowerCase());
      },
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-end gap-3 md:flex-col md:items-start md:gap-0">
            <Typography variant="caption1" className="text-subtle">
              {row.unitModel}
            </Typography>
            <Typography variant="caption1" className="text-subtle">
              {row.brand}
            </Typography>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => chassis.getUnitSerialNumbers(row).join(' '),
      id: 'engines',
      header: t(($) => $.components.table.unit_table.engines),
      cell: (info) => {
        const serialNumbers = chassis.getUnitSerialNumbers(info.row.original);
        return <ProductDesignation serialNumbers={serialNumbers} />;
      },
    },
    {
      accessorKey: 'operatinghours',
      id: 'operatinghours',
      header: t(($) => $.components.table.unit_table.operating_hours),
      cell: (info) => {
        if (isLoadingHours) {
          return <Skeleton variant="rounded-rectangle" height={18} width={100} />;
        }

        const unitsChassisIds = chassis.getUnitsChassisIdentifier(info.row.original);
        const maxHours = Math.max(...unitsChassisIds.map((id) => getOperatingHoursForChassisId(id) || 0));
        return (
          <Typography variant="caption1" className="text-subtle">
            {Math.round(maxHours).toLocaleString()} h
          </Typography>
        );
      },
    },
    {
      accessorKey: 'fleets',
      id: 'fleets',
      header: t(($) => $.components.table.unit_table.fleets),
      cell: (info) => {
        const row = info.row.original;
        return (
          <Suspense>
            <UnitsFleets endCustomerUnitId={row.endCustomerUnitId} />
          </Suspense>
        );
      },
    },
    {
      accessorKey: 'campaigns',
      id: 'campaigns',
      header: t(($) => $.components.table.unit_table.campaigns),
      cell: (info) => {
        const unit = info.row.original;
        return <CampaignStatus unit={unit} showInstallation={showInstallation} />;
      },
    },
  ] as ColumnDef<IUnit>[];

  return { columns, selectedRows, setSelectedRows };
};
export default useUnitsTable;
